import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { useProperties } from '../../system/EntityManager';
import {
  EnumDataExportJobStatus,
  QuarterlyReportDataExportJobFieldsFragment,
  useDeleteQuarterlyReportDataExportJobMutation,
  useQuarterlyReportDataExportJobDownloadUrlLazyQuery,
  useQuarterlyReportDataExportJobsQuery,
  useSubmitQuarterlyReportDataExportJobsMutation,
} from '../../../types/generated-types';

interface QuarterShortcut {
  id: string;
  label: string;
  startDate: string;
  endDate: string;
}

interface PropertyOption {
  _id: string;
  title: string;
}

type ReportArtifact =
  QuarterlyReportDataExportJobFieldsFragment['artifacts'][number];

const ARTIFACT_DOWNLOAD_ORDER = [
  'raw_zip',
  'report_pdf',
  'report_docx',
  'facts_json',
];

function formatDateInput(value: Date): string {
  return value.toISOString().slice(0, 10);
}

function quarterStart(year: number, quarter: number): Date {
  return new Date(Date.UTC(year, (quarter - 1) * 3, 1));
}

function quarterFromOffset(
  now: Date,
  offset: number,
): {
  year: number;
  quarter: number;
} {
  const currentQuarter = Math.floor(now.getUTCMonth() / 3) + 1;
  const zeroBased = now.getUTCFullYear() * 4 + currentQuarter - 1 - offset;
  return {
    year: Math.floor(zeroBased / 4),
    quarter: (zeroBased % 4) + 1,
  };
}

export function getQuarterShortcuts(now = new Date()): QuarterShortcut[] {
  const recentFullQuarters = [1, 2].map((offset) => {
    const { year, quarter } = quarterFromOffset(now, offset);
    const start = quarterStart(year, quarter);
    const end =
      quarter === 4
        ? quarterStart(year + 1, 1)
        : quarterStart(year, quarter + 1);
    return {
      id: `q${quarter}-${year}`,
      label: `Q${quarter} ${year}`,
      startDate: formatDateInput(start),
      endDate: formatDateInput(end),
    };
  });

  const current = quarterFromOffset(now, 0);
  return [
    ...recentFullQuarters,
    {
      id: `q${current.quarter}-${current.year}-qtd`,
      label: `Q${current.quarter} ${current.year} QTD`,
      startDate: formatDateInput(quarterStart(current.year, current.quarter)),
      endDate: formatDateInput(now),
    },
  ];
}

function formatStatus(status: EnumDataExportJobStatus): string {
  switch (status) {
    case EnumDataExportJobStatus.Completed:
      return 'Completed';
    case EnumDataExportJobStatus.Failed:
      return 'Failed';
    case EnumDataExportJobStatus.InProgress:
      return 'In progress';
    case EnumDataExportJobStatus.Queued:
      return 'Queued';
    default:
      return String(status);
  }
}

function formatDateTime(value?: string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function artifactLabel(kind: string): string {
  switch (kind) {
    case 'raw_zip':
      return 'Data ZIP';
    case 'report_pdf':
      return 'Report PDF';
    case 'report_docx':
      return 'Report DOCX';
    case 'facts_json':
      return 'Facts JSON';
    default:
      return kind;
  }
}

function isReportArtifact(
  artifact: ReportArtifact | undefined,
): artifact is ReportArtifact {
  return Boolean(artifact);
}

function orderedArtifacts(job: QuarterlyReportDataExportJobFieldsFragment) {
  const artifactsByKind = new Map(
    job.artifacts.map((artifact) => [artifact.kind, artifact]),
  );
  return ARTIFACT_DOWNLOAD_ORDER.map((kind) =>
    artifactsByKind.get(kind),
  ).filter(isReportArtifact);
}

function canDeleteJob(job: QuarterlyReportDataExportJobFieldsFragment) {
  return (
    job.status === EnumDataExportJobStatus.Completed ||
    job.status === EnumDataExportJobStatus.Failed
  );
}

export function QuarterlyReportsData() {
  const shortcuts = useMemo(() => getQuarterShortcuts(), []);
  const [selectedShortcut, setSelectedShortcut] = useState(shortcuts[0].id);
  const [startDate, setStartDate] = useState(shortcuts[0].startDate);
  const [endDate, setEndDate] = useState(shortcuts[0].endDate);
  const [selectedProperties, setSelectedProperties] = useState<
    PropertyOption[]
  >([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [failureJob, setFailureJob] =
    useState<QuarterlyReportDataExportJobFieldsFragment | null>(null);
  const [deleteJob, setDeleteJob] =
    useState<QuarterlyReportDataExportJobFieldsFragment | null>(null);

  const {
    properties,
    loading: propertiesLoading,
    error: propertiesError,
  } = useProperties();
  const {
    data: jobsData,
    loading: jobsLoading,
    error: jobsError,
    refetch,
  } = useQuarterlyReportDataExportJobsQuery({
    fetchPolicy: 'cache-and-network',
    pollInterval: 10000,
  });
  const [submitJobs, submitState] =
    useSubmitQuarterlyReportDataExportJobsMutation();
  const [deleteExportJob, deleteState] =
    useDeleteQuarterlyReportDataExportJobMutation();
  const [loadDownloadUrl] = useQuarterlyReportDataExportJobDownloadUrlLazyQuery(
    {
      fetchPolicy: 'network-only',
    },
  );

  const propertyOptions = useMemo(
    () =>
      [...properties]
        .map((property) => ({
          _id: property._id,
          title: property.title,
        }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [properties],
  );

  const jobs = jobsData?.quarterlyReportDataExportJobs ?? [];

  const handleShortcut = (
    _event: React.MouseEvent<HTMLElement>,
    id: string,
  ) => {
    if (!id) return;
    const shortcut = shortcuts.find((candidate) => candidate.id === id);
    if (!shortcut) return;
    setSelectedShortcut(id);
    setStartDate(shortcut.startDate);
    setEndDate(shortcut.endDate);
  };

  const handleSubmit = async () => {
    setFormError(null);
    if (!selectedProperties.length) {
      setFormError('Select at least one property.');
      return;
    }

    if (!startDate || !endDate || endDate <= startDate) {
      setFormError('Choose an end date after the start date.');
      return;
    }

    try {
      await submitJobs({
        variables: {
          input: {
            propertyIds: selectedProperties.map((property) => property._id),
            startDate,
            endDate,
          },
        },
      });
      await refetch();
    } catch (error) {
      setFormError(
        error instanceof Error ? error.message : 'Unable to submit export job.',
      );
    }
  };

  const handleDelete = async () => {
    if (!deleteJob) return;

    setFormError(null);
    try {
      await deleteExportJob({
        variables: { id: deleteJob._id },
      });
      setDeleteJob(null);
      await refetch();
    } catch (error) {
      setFormError(
        error instanceof Error ? error.message : 'Unable to delete export job.',
      );
    }
  };

  const handleDownload = async (
    job: QuarterlyReportDataExportJobFieldsFragment,
    artifactKind = 'raw_zip',
  ) => {
    setFormError(null);
    try {
      const result = await loadDownloadUrl({
        variables: { id: job._id, artifactKind },
      });
      const url = result.data?.quarterlyReportDataExportJobDownloadUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      setFormError(
        error instanceof Error
          ? error.message
          : 'Unable to create download link.',
      );
    }
  };

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
            Quarterly Reports Data
          </Typography>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'flex-start' }}
          >
            <Autocomplete
              multiple
              disablePortal
              options={propertyOptions}
              value={selectedProperties}
              loading={propertiesLoading}
              onChange={(_event, value) => setSelectedProperties(value)}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              getOptionLabel={(option) => option.title}
              sx={{ minWidth: { xs: '100%', md: 360 }, flex: 1 }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.title}
                    {...getTagProps({ index })}
                    key={option._id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Properties" />
              )}
            />
            <TextField
              label="Start date"
              type="date"
              value={startDate}
              onChange={(event) => {
                setStartDate(event.target.value);
                setSelectedShortcut('');
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End date"
              type="date"
              value={endDate}
              onChange={(event) => {
                setEndDate(event.target.value);
                setSelectedShortcut('');
              }}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={handleSubmit}
              disabled={submitState.loading}
              sx={{ minHeight: 56, px: 3 }}
            >
              Run
            </Button>
          </Stack>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={selectedShortcut}
            onChange={handleShortcut}
            sx={{ mt: 2 }}
          >
            {shortcuts.map((shortcut) => (
              <ToggleButton key={shortcut.id} value={shortcut.id}>
                {shortcut.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {(formError || propertiesError || jobsError) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError ||
                propertiesError?.message ||
                jobsError?.message ||
                'Unable to load quarterly report data.'}
            </Alert>
          )}
        </Box>

        <TableContainer>
          <Table size="small" aria-label="quarterly reports data jobs">
            <TableHead>
              <TableRow>
                <TableCell>Property</TableCell>
                <TableCell>Range</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job._id}>
                  <TableCell>{job.propertyTitle}</TableCell>
                  <TableCell>
                    {formatDateInput(new Date(job.startDate))} to{' '}
                    {formatDateInput(new Date(job.endDate))}
                  </TableCell>
                  <TableCell>{formatStatus(job.status)}</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    {job.status === EnumDataExportJobStatus.InProgress ||
                    job.status === EnumDataExportJobStatus.Queued ? (
                      <Stack spacing={0.5}>
                        <LinearProgress
                          variant="determinate"
                          value={job.progress ?? 0}
                        />
                        <Typography variant="caption">
                          {job.currentStep || formatStatus(job.status)}
                        </Typography>
                      </Stack>
                    ) : (
                      job.currentStep || formatStatus(job.status)
                    )}
                  </TableCell>
                  <TableCell>{formatDateTime(job.submittedAt)}</TableCell>
                  <TableCell align="right">
                    {job.status === EnumDataExportJobStatus.Completed && (
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                        flexWrap="wrap"
                      >
                        {orderedArtifacts(job).map((artifact) => (
                          <Button
                            key={artifact.kind}
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownload(job, artifact.kind)}
                            aria-label={`Download ${artifactLabel(
                              artifact.kind,
                            )} ${job.propertyTitle}`}
                          >
                            {artifactLabel(artifact.kind)}
                          </Button>
                        ))}
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => setDeleteJob(job)}
                          aria-label={`Delete report job ${job.propertyTitle}`}
                        >
                          Delete
                        </Button>
                      </Stack>
                    )}
                    {job.status === EnumDataExportJobStatus.Failed && (
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                        flexWrap="wrap"
                      >
                        <Button
                          size="small"
                          color="error"
                          startIcon={<ErrorOutlineIcon />}
                          onClick={() => setFailureJob(job)}
                          aria-label={`View failure ${job.propertyTitle}`}
                        >
                          Details
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => setDeleteJob(job)}
                          aria-label={`Delete report job ${job.propertyTitle}`}
                        >
                          Delete
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!jobs.length && !jobsLoading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    No quarterly data export jobs.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Dialog open={Boolean(failureJob)} onClose={() => setFailureJob(null)}>
        <DialogTitle>{failureJob?.propertyTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography>{failureJob?.errorMessage || 'Job failed.'}</Typography>
            {failureJob?.errorDetails && (
              <Typography variant="body2" color="text.secondary">
                {failureJob.errorDetails}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailureJob(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteJob)}
        onClose={() => setDeleteJob(null)}
        aria-labelledby="delete-quarterly-report-job-title"
      >
        <DialogTitle id="delete-quarterly-report-job-title">
          Delete Report Job
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography>
              Delete this report job and remove its generated AWS artifacts?
            </Typography>
            {deleteJob && (
              <Typography variant="body2" color="text.secondary">
                {deleteJob.propertyTitle}:{' '}
                {formatDateInput(new Date(deleteJob.startDate))} to{' '}
                {formatDateInput(new Date(deleteJob.endDate))}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteJob(null)}>Cancel</Button>
          <Button
            color="error"
            onClick={handleDelete}
            disabled={
              !deleteJob || !canDeleteJob(deleteJob) || deleteState.loading
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
